import React, { useEffect, useState } from "react";
import { AlertTriangle, CloudRain, Wind, Zap, Tornado, CloudSnow, Flame, Snowflake } from "lucide-react";
import api from "@/lib/api";

// Fallback for first paint / API outage — clearly labelled
const FALLBACK_STORMS = [
  { event: "EF2 Tornado Watch", area: "Tulsa County, OK", severity: "SEVERE" },
  { event: "Flash Flood Warning", area: "Harris County, TX", severity: "SEVERE" },
  { event: "70 mph Wind Advisory", area: "Jefferson Parish, LA", severity: "ELEVATED" },
  { event: "Severe Thunderstorm Warning", area: "Mecklenburg, NC", severity: "SEVERE" },
  { event: "Hail · 2.5\"", area: "Denver Metro, CO", severity: "ELEVATED" },
  { event: "Ice Storm Watch", area: "Polk County, IA", severity: "ELEVATED" },
  { event: "Hurricane Cat 2", area: "Gulf Coast, FL Pan.", severity: "SEVERE" },
];

function pickIcon(event = "") {
  const e = event.toLowerCase();
  if (e.includes("tornado")) return Tornado;
  if (e.includes("hurricane") || e.includes("tropical")) return AlertTriangle;
  if (e.includes("flood") || e.includes("rain")) return CloudRain;
  if (e.includes("wind")) return Wind;
  if (e.includes("thunderstorm") || e.includes("lightning")) return Zap;
  if (e.includes("ice")) return Snowflake;
  if (e.includes("snow") || e.includes("blizzard") || e.includes("winter")) return CloudSnow;
  if (e.includes("fire") || e.includes("red flag") || e.includes("heat")) return Flame;
  return AlertTriangle;
}

function formatEta(eta) {
  if (eta == null) return "ACTIVE";
  if (eta <= 0.5) return "ACTIVE";
  if (eta < 1) return `${Math.round(eta * 60)} MIN`;
  if (eta < 24) return `${Math.round(eta)} H`;
  return `${Math.round(eta / 24)} D`;
}

export default function LiveTicker() {
  const [storms, setStorms] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/weather/ticker?limit=14");
        if (cancelled) return;
        if (data?.alerts?.length) {
          setStorms(data.alerts);
        }
      } catch (err) {
        console.warn("LiveTicker fetch failed, using fallback list", err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const list = storms.length > 0 ? storms : FALLBACK_STORMS;
  const items = [...list, ...list];

  return (
    <div
      className="relative w-full border-y border-white/5 bg-[#0a0a0b] py-3 overflow-hidden"
      data-testid="live-ticker"
    >
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 hidden sm:flex items-center gap-2 bg-[#dc2626] px-3 py-1">
        <span className="live-dot bg-white" style={{ boxShadow: "none" }} />
        <span className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-white">
          {loaded && storms.length > 0 ? "NWS LIVE" : "NWS"}
        </span>
      </div>
      <div className="marquee-track gap-12 px-6 sm:pl-32">
        {items.map((s, i) => {
          const Icon = pickIcon(s.event || s.sev);
          const crit = (s.severity || "").toUpperCase() === "SEVERE";
          const label = (s.event || s.sev || "Weather alert").toUpperCase();
          const area = s.area || s.loc || "United States";
          const chg = s.eta_hours !== undefined ? formatEta(s.eta_hours) : (s.chg || "ACTIVE");
          const key = `${s.id || `${label}-${area}`}-${i < items.length / 2 ? "a" : "b"}`;
          return (
            <div key={key} className="flex items-center gap-3 whitespace-nowrap" data-testid="ticker-item">
              <Icon size={14} strokeWidth={1.5} className={crit ? "text-[#dc2626]" : "text-zinc-300"} />
              <span className={`font-mono-apex text-[11px] tracking-[0.15em] ${crit ? "text-white" : "text-zinc-300"}`}>
                {label}
              </span>
              <span className="font-mono-apex text-[11px] text-zinc-500">{area}</span>
              <span className={`font-mono-apex text-[11px] ${crit ? "text-[#dc2626]" : "text-zinc-400"}`}>
                {chg}
              </span>
              <span className="text-zinc-700">·</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
